import React from 'react';
import Modal from './Modal';

interface ValidationResult {
  status: 'checking' | 'preApproved' | 'rejected' | 'pendingReview';
  rejectionReasons?: string[];
  fileName?: string;
}

interface Props {
  result: ValidationResult | null;
  onClose: () => void;
  onReUpload?: () => void;
  onRequestReview?: () => void;
}

export default function DocumentValidationPopup({ result, onClose, onReUpload, onRequestReview }: Props) {
  if (!result) return null;

  const isChecking = result.status === 'checking';

  return (
    <Modal onClose={isChecking ? undefined : onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center"
        onClick={e => e.stopPropagation()}
        style={{ animation: 'validation-popup-in 0.35s cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* ── Checking state ── */}
        {result.status === 'checking' && (
          <>
            <div className="mb-4 flex justify-center">
              <div className="relative h-16 w-16">
                <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="h-7 w-7 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <h3 className="text-lg font-extrabold text-slate-800 mb-1">Checking file...</h3>
            <p className="text-sm text-slate-500">
              Our system is reviewing <span className="font-semibold text-slate-700">{result.fileName}</span> for quality and compliance.
            </p>
            <div className="mt-5 flex justify-center gap-1">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="h-2 w-2 rounded-full bg-brand-primary"
                  style={{ animation: `validation-bounce 1.2s ${i * 0.2}s infinite` }}
                />
              ))}
            </div>
          </>
        )}

        {/* ── Pre-Approved state ── */}
        {result.status === 'preApproved' && (
          <>
            <div className="mb-4 flex justify-center">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center"
                style={{ animation: 'validation-pop-in 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                <svg className="h-9 w-9 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-extrabold text-green-700 mb-1">Document Pre-Approved</h3>
            <p className="text-sm text-slate-500 mb-5">
              <span className="font-semibold text-slate-700">{result.fileName}</span> passed our automated checks.
              It may still be subject to manual review.
            </p>
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-xl transition-all cursor-pointer"
            >
              Continue
            </button>
          </>
        )}

        {/* ── Rejected state ── */}
        {result.status === 'rejected' && (
          <>
            <div className="mb-4 flex justify-center">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center"
                style={{ animation: 'validation-pop-in 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                <svg className="h-9 w-9 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-extrabold text-red-700 mb-1">Document Not Approved</h3>
            <p className="text-sm text-slate-500 mb-3">
              <span className="font-semibold text-slate-700">{result.fileName}</span> did not pass our automated checks.
            </p>
            {result.rejectionReasons && result.rejectionReasons.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-left">
                <p className="text-caption font-bold text-red-700 mb-1.5">Reasons:</p>
                <ul className="space-y-1">
                  {result.rejectionReasons.map((reason, i) => (
                    <li key={i} className="text-caption text-red-600 flex items-start gap-1.5">
                      <span className="mt-0.5 shrink-0">•</span>
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-col gap-2">
              {onReUpload && (
                <button
                  onClick={onReUpload}
                  className="w-full py-2.5 btn-primary  text-white text-sm font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Re-upload Document
                </button>
              )}
              {onRequestReview && (
                <button
                  onClick={onRequestReview}
                  className="w-full py-2.5 border border-amber-300 hover:bg-amber-50 text-amber-700 text-sm font-bold rounded-xl transition-all cursor-pointer"
                >
                  Request Manual Review
                </button>
              )}
              <button
                onClick={onClose}
                className="text-sm text-slate-400 hover:text-slate-600 font-semibold cursor-pointer py-1"
              >
                Close
              </button>
            </div>
          </>
        )}

        {/* ── Pending Admin Review state ── */}
        {result.status === 'pendingReview' && (
          <>
            <div className="mb-4 flex justify-center">
              <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center"
                style={{ animation: 'validation-pop-in 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                <svg className="h-9 w-9 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-extrabold text-amber-700 mb-1">Pending Admin Review</h3>
            <p className="text-sm text-slate-500 mb-5">
              <span className="font-semibold text-slate-700">{result.fileName}</span> needs to be reviewed by an administrator before it can be approved.
            </p>
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold rounded-xl transition-all cursor-pointer"
            >
              Got it
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}
